/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {
  useMemo
} from 'react';
import {Divider} from '@material-ui/core';
import MenuList from '@material-ui/core/MenuList';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import MenuOption from '../components/menus/MenuOption';
import {SelectedRows} from './IonGridUtils';

const useStyles = makeStyles(() => ({
  menu : {
    paddingBottom : '0px !important',
    boxShadow     : 'rgba(0, 0, 0, 0.16) 0px 3px 10px'
  }
}));

export interface RowSelectionMenuProps extends SelectedRows {
  onKeyDown: (event: KeyboardEvent) => void;
  onPageRowsSelect: () => void;
  onTotalRowsSelect: () => void;
  onDeselectAllRows: () => void;
  open: boolean;
  pageCount: number;
}

/**
 * Menu for selecting rows, either all rows on the page, all total rows, or deselect all rows.
 *
 * ```typescript
 *
 *  import {RowSelectionMenu} from '@ion/dls/src/grid/';
 *
 *  <RowSelectionMenu open={true}
 *    onPageRowsSelect={selectRowsOnPage}
 *    onTotalRowsSelect={selectTotalRows}
 *    pageCount={pageSize}
 *    totalCount={totalCount}
 *    onClick={onClick}
 *    onKeyDown={onKeyDown} />
 *
 * ```
 */
export default function RowSelectionMenu({
  onKeyDown,
  onPageRowsSelect,
  onTotalRowsSelect,
  onDeselectAllRows,
  open,
  totalRowCount,
  pageCount
}: RowSelectionMenuProps) {
  const classes       = useStyles();
  const showAllOption = totalRowCount > pageCount;
  const translations  = useMemo(() => translate('grids:selectionMenu', {
    returnObjects : true,
    pageCount     : showAllOption ? pageCount : totalRowCount,
    totalRowCount
  }), [pageCount, totalRowCount]);

  return (
    <MenuList autoFocus
      autoFocusItem={open}
      id="menu-list-grow"
      onKeyDown={onKeyDown}
      classes={{
        root : classes.menu
      }}>
      <MenuOption onClick={onPageRowsSelect} label={translations.selectOnPage} />
      <MenuOption onClick={onTotalRowsSelect} label={translations.selectAll} />
      <Divider />
      <MenuOption onClick={onDeselectAllRows} label={translations.deselectAll} />
    </MenuList>
  );
}
